import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { agruparReservas } from "@/lib/reservasUtils";
import { Loader2, Pencil } from "lucide-react";

const WEEKDAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

function parseDate(yyyymmdd) {
    const [y, m, d] = yyyymmdd.split("-").map(Number);
    return new Date(y, m - 1, d);
}

function fmtFecha(yyyymmdd) {
    const d = parseDate(yyyymmdd);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((d - today) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Hoy";
    if (diffDays === 1) return "Mañana";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${WEEKDAYS[d.getDay()]} ${dd}/${mm}`;
}

function toMin(hhmm) {
    const [h, m] = hhmm.split(":").map(Number);
    return h * 60 + m;
}
function fromMin(min) {
    return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

const DURACIONES = [
    { v: 30, l: "30 min" },
    { v: 60, l: "1 h" },
    { v: 90, l: "1 h 30" },
    { v: 120, l: "2 h" },
    { v: 180, l: "3 h" },
    { v: 240, l: "4 h" },
];

// Genera opciones de hora de inicio cada 30 min dentro del horario de la sala.
function buildHoras(sala) {
    if (!sala) return [];
    const out = [];
    const ini = toMin(sala.horario_inicio);
    const fin = toMin(sala.horario_fin);
    for (let m = ini; m < fin; m += 30) out.push(fromMin(m));
    return out;
}

export default function MisReservas() {
    const [items, setItems] = useState([]);
    const [salas, setSalas] = useState([]);
    const [busyKey, setBusyKey] = useState(null);
    const [confirmGroup, setConfirmGroup] = useState(null);
    const [editGroup, setEditGroup] = useState(null);

    const load = async () => {
        const [a, b] = await Promise.all([api.get("/reservas/mias"), api.get("/salas")]);
        setItems(a.data);
        setSalas(b.data);
    };
    useEffect(() => { load(); }, []);

    const salaName = (id) => salas.find((s) => s.sala_id === id)?.nombre || "—";

    const grupos = useMemo(() => agruparReservas(items), [items]);

    const cancelGroup = async (g) => {
        setBusyKey(g.key);
        const ids = g.reservas.map((r) => r.reserva_id);
        try {
            await Promise.all(ids.map((id) => api.post(`/reservas/${id}/cancelar`)));
            setItems((arr) => arr.filter((x) => !ids.includes(x.reserva_id)));
            setConfirmGroup(null);
            toast.success(ids.length === 1 ? "Reserva cancelada" : `${ids.length} bloques cancelados`);
        } catch (e) {
            toast.error(formatApiError(e));
            await load();
        } finally {
            setBusyKey(null);
        }
    };

    return (
        <div className="max-w-2xl">
            <PageHeader title="Mis Reservas" subtitle="Tus próximas reservas activas." />

            {grupos.length === 0 ? (
                <EmptyState title="No tenés reservas próximas." description="Cuando reserves una sala aparecerá acá." />
            ) : (
                <div className="grid gap-2.5">
                    {grupos.map((g) => {
                        const n = g.reservas.length;
                        const slotMin = toMin(g.reservas[0].hora_fin) - toMin(g.reservas[0].hora_inicio);
                        return (
                            <Card key={g.key} data-testid={`mi-reserva-${g.key}`} className="overflow-hidden">
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-heading font-semibold text-base leading-tight truncate">
                                                {salaName(g.sala_id)}
                                            </p>
                                            <p className="mt-1 text-sm text-foreground">
                                                <span className="font-medium">{fmtFecha(g.fecha)}</span>
                                                <span className="text-muted-foreground"> · </span>
                                                <span className="font-mono">{g.hora_inicio} a {g.hora_fin}</span>
                                            </p>
                                            {n > 1 && (
                                                <p className="mt-0.5 text-[11px] text-muted-foreground">
                                                    {n} módulos de {slotMin} min
                                                </p>
                                            )}
                                            {g.motivo && (
                                                <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                                                    Motivo: {g.motivo}
                                                </p>
                                            )}
                                        </div>
                                        <Badge className="bg-accent/15 text-accent hover:bg-accent/15 border border-accent/30 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider shrink-0">
                                            Confirmada
                                        </Badge>
                                    </div>
                                    <div className="mt-3 flex justify-end gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-8 px-3"
                                            onClick={() => setEditGroup(g)}
                                            disabled={busyKey === g.key}
                                            data-testid={`edit-${g.key}`}
                                        >
                                            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 px-3"
                                            onClick={() => setConfirmGroup(g)}
                                            disabled={busyKey === g.key}
                                            data-testid={`cancel-${g.key}`}
                                        >
                                            {busyKey === g.key ? "Cancelando…" : "Cancelar"}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            <AlertDialog open={!!confirmGroup} onOpenChange={(o) => !o && !busyKey && setConfirmGroup(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Querés cancelar esta reserva?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmGroup && (
                                <>
                                    {salaName(confirmGroup.sala_id)} · {fmtFecha(confirmGroup.fecha)} ·{" "}
                                    <span className="font-mono">{confirmGroup.hora_inicio} a {confirmGroup.hora_fin}</span>
                                    {confirmGroup.reservas.length > 1 && (
                                        <> · se van a liberar {confirmGroup.reservas.length} bloques.</>
                                    )}
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={!!busyKey} data-testid="cancel-no">No</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => confirmGroup && cancelGroup(confirmGroup)}
                            disabled={!!busyKey}
                            className="bg-destructive hover:bg-destructive/90"
                            data-testid="cancel-yes"
                        >
                            {busyKey ? "Cancelando…" : "Sí, cancelar"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <EditarReservaDialog
                group={editGroup}
                salas={salas}
                onClose={() => setEditGroup(null)}
                onSaved={async () => {
                    setEditGroup(null);
                    await load();
                }}
            />
        </div>
    );
}

// ─── Modal de edición ──────────────────────────────────────────────────────
function EditarReservaDialog({ group, salas, onClose, onSaved }) {
    const open = !!group;
    const [salaId, setSalaId] = useState("");
    const [fecha, setFecha] = useState("");
    const [hora, setHora] = useState("");
    const [duracion, setDuracion] = useState(60);
    const [busy, setBusy] = useState(false);

    const today = new Date().toISOString().slice(0, 10);

    // Reset cuando cambia el grupo a editar
    useEffect(() => {
        if (!group) return;
        setSalaId(group.sala_id);
        setFecha(group.fecha);
        setHora(group.hora_inicio);
        setDuracion(toMin(group.hora_fin) - toMin(group.hora_inicio));
    }, [group]);

    const sala = useMemo(() => salas.find((s) => s.sala_id === salaId) || null, [salas, salaId]);
    const horas = useMemo(() => buildHoras(sala), [sala]);

    const horaFin = useMemo(() => {
        if (!hora) return "";
        return fromMin(toMin(hora) + duracion);
    }, [hora, duracion]);

    // Validaciones UI mínimas
    const fueraDeHorario = useMemo(() => {
        if (!sala || !hora) return false;
        const fin = toMin(hora) + duracion;
        return fin > toMin(sala.horario_fin);
    }, [sala, hora, duracion]);

    const submit = async () => {
        if (!group || !salaId || !fecha || !hora) return;
        if (fueraDeHorario) {
            toast.error("La duración supera el horario de la sala.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/reservas/editar", {
                ids: group.reservas.map((r) => r.reserva_id),
                sala_id: salaId,
                fecha,
                hora_inicio: hora,
                hora_fin: horaFin,
            });
            toast.success("Reserva actualizada");
            await onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Editar reserva</DialogTitle>
                    <DialogDescription>
                        Cambiá sala, fecha, horario o duración. La disponibilidad se valida al guardar.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs">Sala</Label>
                        <Select value={salaId} onValueChange={setSalaId}>
                            <SelectTrigger data-testid="edit-sala"><SelectValue placeholder="Elegí sala" /></SelectTrigger>
                            <SelectContent>
                                {salas.filter((s) => s.activa).map((s) => (
                                    <SelectItem key={s.sala_id} value={s.sala_id}>{s.nombre}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1.5">
                        <Label className="text-xs">Fecha</Label>
                        <Input
                            type="date"
                            value={fecha}
                            min={today}
                            onChange={(e) => setFecha(e.target.value || today)}
                            data-testid="edit-fecha"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1.5">
                            <Label className="text-xs">Hora inicio</Label>
                            <Select value={hora} onValueChange={setHora}>
                                <SelectTrigger data-testid="edit-hora"><SelectValue placeholder="HH:MM" /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {horas.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Duración</Label>
                            <Select value={String(duracion)} onValueChange={(v) => setDuracion(parseInt(v, 10))}>
                                <SelectTrigger data-testid="edit-duracion"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {DURACIONES.map((d) => (
                                        <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {horaFin && (
                        <p className="text-[11px] text-muted-foreground font-mono">
                            Nuevo horario: <span className={fueraDeHorario ? "text-destructive" : "text-foreground"}>{hora} a {horaFin}</span>
                            {fueraDeHorario && <span className="block text-destructive">Supera el cierre de la sala.</span>}
                        </p>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy} data-testid="edit-cancelar">Cancelar</Button>
                    <Button onClick={submit} disabled={busy || fueraDeHorario} data-testid="edit-guardar">
                        {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Guardando…</> : "Guardar cambios"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
